import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Spotlight, toast } from '@mlm/ui';
import type { MemberTutorialKey } from '@mlm/shared';
import { getOnboarding, updateMemberTutorial, type OnboardingState } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';
import { useActivationKits } from '../../lib/hooks/use-activation-kits';
import { useMySlots } from '../../lib/hooks/use-my-slots';
import { UNPAID_DIALOG_SESSION_FLAG } from '../../lib/lock-inputs';
import { decideAutoRun, resolveStep, routeMatches } from '../../lib/tutorial/decide';
import { buildSteps, tutorialTitle } from '../../lib/tutorial/steps';
import { useTutorialStore, type TutorialMode } from '../../lib/tutorial/store';
import { useTutorialEnabled } from '../../lib/tutorial/use-tutorial-enabled';
import { useBrand } from '../../storefront/lib/hooks';
import { KitPickerDialog } from '../kit-picker-dialog';
import { TutorialMenuDialog } from './tutorial-menu';

/** How long a tutorial page may show no step before the member gets a way out. */
const STUCK_AFTER_MS = 3000;

/** The element a step points at, if it is really on screen. */
function findTarget(key: string): Element | null {
  for (const el of document.querySelectorAll(`[data-tour="${key}"]`)) {
    if (el.getClientRects().length > 0) return el;
  }
  return null;
}

/** A dialog that isn't ours: the terms gate, the address form, the mobile menu. */
function openForeignDialogs(): Element[] {
  return Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]')).filter(
    (el) => !el.closest('[data-tutorial-ui]') && el.getClientRects().length > 0,
  );
}

/**
 * Runs the guided member tutorials. Mounted once, above the routes, because a
 * tutorial crosses all three page shells: the portal, the storefront checkout
 * and the bare payment page.
 */
export function TutorialHost() {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const enabled = useTutorialEnabled();
  const active = useTutorialStore((s) => s.active);

  // Signed out or another account in this tab: nothing carries over.
  useEffect(() => {
    if (active && active.userId !== userId) useTutorialStore.getState().stop();
  }, [active, userId]);

  if (!userId || !enabled) return null;
  return <TutorialRunner userId={userId} />;
}

function TutorialRunner({ userId }: { userId: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const brand = useBrand();
  const impersonating = useAuthStore((s) => !!s.user?.impersonator);
  const { hasAnyActiveSlot } = useMySlots();

  const active = useTutorialStore((s) => s.active);
  const ended = useTutorialStore((s) => s.ended);
  const { start, acknowledge, markFinished, stop } = useTutorialStore.getState();
  const pathname = location.pathname;

  // The same query as the dashboard's Getting started card. There is no push
  // channel to the member app, so a member waiting on payment approval polls.
  const onboardingQ = useQuery({
    queryKey: ['onboarding'],
    queryFn: getOnboarding,
    refetchOnWindowFocus: true,
    refetchInterval: (q) =>
      q.state.data?.tutorial.state && q.state.data.stage === 'payment_pending' ? 60_000 : false,
  });
  const onboarding = onboardingQ.data;
  const signals = onboarding
    ? {
        stage: onboarding.stage,
        activationCodeCount: onboarding.signals.activationCodeCount,
        needsPayment: onboarding.signals.pendingKitOrderNeedsPayment,
      }
    : null;

  // Only worth fetching when something is about to ask for the kit list.
  const menuOpen = useTutorialStore((s) => s.menuOpen);
  const kitOwed =
    onboarding?.tutorial.state?.kit === null &&
    (onboarding.stage === 'registered' || onboarding.stage === 'payment_pending');
  const { kits, isLoading: kitsLoading } = useActivationKits(menuOpen || kitOwed);

  // Walking to the next page can change the stage (an order was just placed).
  useEffect(() => {
    if (active) void qc.invalidateQueries({ queryKey: ['onboarding'] });
  }, [active, pathname, qc]);

  const save = useCallback(
    (tutorial: MemberTutorialKey, status: 'started' | 'done' | 'skipped') => {
      if (impersonating) return;
      updateMemberTutorial({ tutorial, status })
        .then(({ state }) => {
          qc.setQueryData<OnboardingState>(['onboarding'], (old) =>
            old ? { ...old, tutorial: { ...old.tutorial, state } } : old,
          );
        })
        // `ended` in the store already stops it reappearing this session; the
        // next session asks again, which is the right failure for a hint.
        .catch(() => undefined);
    },
    [impersonating, qc],
  );

  const begin = useCallback(
    (key: MemberTutorialKey, mode: TutorialMode) => {
      start({ userId, key, mode });
      // The soft "you have unpaid orders" prompt would sit on top of the guide
      // that is already taking them to pay.
      if (key === 'kit') window.sessionStorage.setItem(UNPAID_DIALOG_SESSION_FLAG, '1');
    },
    [start, userId],
  );

  // ── Starting and closing without being asked ──
  const handled = useRef(new Set<string>());
  useEffect(() => {
    if (!onboarding || !signals) return;
    const decision = decideAutoRun({
      state: onboarding.tutorial.state,
      signals,
      pathname,
      impersonating,
      activeKey: active?.key ?? null,
      activeMode: active?.mode ?? null,
      ended,
      kitsAvailable: !kitsLoading && kits.length > 0,
    });
    if (decision.kind === 'start') {
      begin(decision.key, 'live');
    } else if (decision.kind === 'close') {
      const stamp = `close:${decision.key}`;
      if (handled.current.has(stamp)) return;
      handled.current.add(stamp);
      if (active?.key === decision.key && active.mode === 'live') stop();
      save(decision.key, 'done');
    }
    // `signals` is rebuilt each render; its parts are the real dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onboarding,
    pathname,
    impersonating,
    active?.key,
    active?.mode,
    ended,
    kitsLoading,
    kits.length,
    begin,
    save,
    stop,
  ]);

  // ── Which step, read from the page ──
  const steps = useMemo(
    () =>
      active
        ? buildSteps(active.key, {
            brand,
            mode: active.mode,
            pendingOrderId: onboarding?.signals.pendingKitOrderId ?? null,
            needsPayment: onboarding?.signals.pendingKitOrderNeedsPayment ?? false,
            slotMayAutoCreate: onboarding?.tutorial.slotMayAutoCreate ?? false,
          })
        : [],
    [active, brand, onboarding],
  );

  const [view, setView] = useState<{
    stepId: string | null;
    target: Element | null;
    blocked: boolean;
  }>({ stepId: null, target: null, blocked: false });

  useEffect(() => {
    if (!active) {
      setView({ stepId: null, target: null, blocked: false });
      return;
    }
    let frame = 0;
    const read = () => {
      frame = 0;
      const step = resolveStep(steps, pathname, active.seen, (t) => !!findTarget(t));
      const target = step?.target ? findTarget(step.target) : null;
      // Step aside for a dialog the member opened (the address form, say), unless
      // the step is pointing at something inside that dialog.
      const blocked = openForeignDialogs().some((d) => !target || !d.contains(target));
      setView((v) =>
        v.stepId === (step?.id ?? null) && v.target === target && v.blocked === blocked
          ? v
          : { stepId: step?.id ?? null, target, blocked },
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-tour', 'data-state', 'class', 'hidden'],
    });
    window.addEventListener('resize', schedule);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [active, pathname, steps]);

  const step = steps.find((s) => s.id === view.stepId) ?? null;

  // Reaching the last step records the outcome, so closing the tab there is fine.
  useEffect(() => {
    if (!active || !step?.final || active.finished) return;
    markFinished();
    if (active.mode === 'live') save(active.key, 'done');
  }, [active, markFinished, save, step]);

  // The closing step's screen has gone (the member moved on), so the guide is done.
  useEffect(() => {
    if (active?.finished && !step && !view.blocked) stop();
  }, [active?.finished, step, stop, view.blocked]);

  // ── A page with no step: give them a way back or a way out ──
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    setStuck(false);
    if (!active || step || view.blocked) return;
    const timer = window.setTimeout(() => setStuck(true), STUCK_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [active, pathname, step, view.blocked]);

  // ── Practice on a member who already has a slot ──
  // The dashboard's kit grid only exists before the first slot, so practice shows
  // the same grid in the existing Browse dialog.
  const [pickerClosed, setPickerClosed] = useState(false);
  const showPicker =
    !!active &&
    active.key === 'kit' &&
    active.mode === 'practice' &&
    hasAnyActiveSlot &&
    pathname === '/dashboard' &&
    active.seen.includes('kit.welcome') &&
    !pickerClosed;

  /** A practice purchase leaves nothing to look at once its cart is put back. */
  function leavePracticeCheckout() {
    if (active?.mode === 'practice' && pathname === '/checkout') navigate('/dashboard');
  }

  function skip() {
    if (!active) return;
    const key = active.key;
    const wasLive = active.mode === 'live';
    stop();
    leavePracticeCheckout();
    if (wasLive) {
      save(key, 'skipped');
      toast.info('You can run this again from Tutorial in the menu.');
    }
  }

  function resume() {
    if (!active) return;
    setPickerClosed(false);
    setStuck(false);
    if (active.key === 'activate') {
      navigate('/code-vault');
    } else {
      const orderId = onboarding?.signals.pendingKitOrderId;
      navigate(active.mode === 'live' && orderId ? `/orders/${orderId}` : '/dashboard');
    }
  }

  function pressNext() {
    if (!active || !step) return;
    if (step.final) {
      stop();
      leavePracticeCheckout();
      return;
    }
    acknowledge(step.id);
    if (step.go && !routeMatches(step.go, pathname)) navigate(step.go);
  }

  const heading = active ? tutorialTitle(active.key, brand) : '';
  const practice = active?.mode === 'practice';

  return (
    <>
      <TutorialMenuDialog
        signals={signals}
        kitsAvailable={kitsLoading || kits.length > 0}
        impersonating={impersonating}
        onStart={(key, mode) => {
          if (active) stop();
          setPickerClosed(false);
          begin(key, mode);
          if (mode === 'live') save(key, 'started');
          navigate(key === 'kit' ? '/dashboard' : '/code-vault');
        }}
      />

      {showPicker && <KitPickerDialog open onClose={() => setPickerClosed(true)} />}

      {active && step && !view.blocked && (
        <Spotlight target={view.target} interaction={step.interaction} label={heading}>
          <div
            key={step.id}
            className="animate-in fade-in-0 duration-150 motion-reduce:animate-none"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">{heading}</span>
              {practice && <Badge variant="secondary">Practice</Badge>}
            </div>
            <h2 className="mt-1 text-base font-semibold">{step.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground" aria-live="polite">
              {step.body}
            </p>
            <div className="mt-3 flex items-center justify-between gap-3">
              {step.final ? (
                <span />
              ) : (
                <button
                  type="button"
                  onClick={skip}
                  className="-my-2 -ml-1 min-h-11 rounded-md px-1 text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  {practice ? 'End practice' : 'Skip tutorial'}
                </button>
              )}
              {step.next && (
                <Button size="sm" onClick={pressNext}>
                  {step.next}
                </Button>
              )}
            </div>
          </div>
        </Spotlight>
      )}

      {active && !step && stuck && !view.blocked && !active.finished && (
        <div
          data-tutorial-ui
          className="fixed inset-x-3 bottom-20 z-[70] mx-auto flex max-w-sm items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3 shadow-elevated md:bottom-6"
        >
          <span className="text-sm font-medium">Tutorial paused</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={skip}>
              {practice ? 'End' : 'Skip'}
            </Button>
            <Button size="sm" onClick={resume}>
              Resume
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
